/**
 * Supplier items — `/purchasing/supplier-items`.
 *
 * Purchasing defaults per (supplier, variant): supplier SKU, MOQ, pack size,
 * lead time, reorder points and the over-receipt tolerance. `last_cost` is
 * shown but never editable — receipt history alone maintains it, and the
 * contract's update body has no field for it. Edits round-trip the ETag
 * (`If-Match`), so a concurrent change opens the conflict dialog instead of
 * silently overwriting.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Button,
  Checkbox,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { ConflictDialog } from "@/components/form";
import { DataTable, dateTimeCell, textCell } from "@/components/data-table";
import type { SortState } from "@/components/data-table";
import { formatQuantity } from "@/lib/format";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  createSupplierItem,
  deleteSupplierItem,
  getSupplierItem,
  updateSupplierItem,
  type SupplierItem,
  type SupplierItemCreate,
} from "../api";
import { purchasingKeys, useSupplierItemList, type SupplierItemFilters } from "../queries";
import { useSupplierNames, useVariantSkus } from "../components/lookup-names";
import { DecimalInput } from "../components/decimal-input";
import { handleProblem, type FieldErrors } from "../problem-errors";
import { COST_SCALE, PCT_SCALE, QTY_SCALE, toScale } from "../decimal";

const ALL = "__all__";

interface ItemDraft {
  supplier_id: string;
  variant_id: string;
  supplier_sku: string;
  currency: string;
  minimum_order_qty: string;
  pack_size: string;
  lead_time_days: string;
  is_preferred: boolean;
  is_active: boolean;
  reorder_point_qty: string;
  safety_stock_qty: string;
  target_stock_qty: string;
  default_order_qty: string;
  over_receipt_tolerance_pct: string;
}

const EMPTY_DRAFT: ItemDraft = {
  supplier_id: "",
  variant_id: "",
  supplier_sku: "",
  currency: "SAR",
  minimum_order_qty: "1.000000",
  pack_size: "1.000000",
  lead_time_days: "0",
  is_preferred: false,
  is_active: true,
  reorder_point_qty: "",
  safety_stock_qty: "",
  target_stock_qty: "",
  default_order_qty: "",
  over_receipt_tolerance_pct: "",
};

export interface SupplierItemsPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  canReadSuppliers?: boolean;
  canReadVariants?: boolean;
  pageLimit?: number;
}

export function SupplierItemsPage({
  canCreate = false,
  canUpdate = false,
  canDelete = false,
  canReadSuppliers = true,
  canReadVariants = true,
  pageLimit,
}: SupplierItemsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [preferred, setPreferred] = useState<string>(ALL);
  const [sort, setSort] = useState<SortState>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingEtag, setEditingEtag] = useState<string | null>(null);
  const [draft, setDraft] = useState<ItemDraft>(EMPTY_DRAFT);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [conflict, setConflict] = useState(false);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);

  const { supplierName, parties } = useSupplierNames(canReadSuppliers);
  const { variantSku, variants } = useVariantSkus(canReadVariants);

  const filters = useMemo<SupplierItemFilters>(() => {
    const built: SupplierItemFilters = {};
    if (supplierId !== null && supplierId !== "") built.supplier_id = supplierId;
    if (q !== "") built.q = q;
    if (preferred !== ALL) built.preferred = preferred === "yes";
    return built;
  }, [supplierId, q, preferred]);

  const hasFilters = Object.keys(filters).length > 0;
  const query = useSupplierItemList(filters, sort, pageLimit);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: purchasingKeys.supplierItems.all });
  };

  const onMutationError = (error: unknown): void => {
    handleProblem(error, t, {
      onConflict: () => setConflict(true),
      onFieldErrors: setFieldErrors,
      onCode: (code, apiError) => {
        if (code !== "duplicate_supplier_item") return false;
        const existing = (apiError.problem as unknown as { existing_supplier_item_id?: string })
          .existing_supplier_item_id;
        setDuplicateOf(existing ?? "");
        return true;
      },
    });
  };

  const loadMutation = useMutation({
    mutationFn: getSupplierItem,
    onSuccess: ({ data, etag }) => {
      setEditingId(data.id);
      setEditingEtag(etag);
      setDraft({
        supplier_id: data.supplier_id,
        variant_id: data.variant_id,
        supplier_sku: data.supplier_sku,
        currency: data.currency,
        minimum_order_qty: data.minimum_order_qty,
        pack_size: data.pack_size,
        lead_time_days: String(data.lead_time_days),
        is_preferred: data.is_preferred,
        is_active: data.is_active,
        reorder_point_qty: data.reorder_point_qty,
        safety_stock_qty: data.safety_stock_qty,
        target_stock_qty: data.target_stock_qty ?? "",
        default_order_qty: data.default_order_qty ?? "",
        over_receipt_tolerance_pct: data.over_receipt_tolerance_pct,
      });
      setFieldErrors({});
      setDuplicateOf(null);
      setEditorOpen(true);
    },
    onError: onMutationError,
  });

  const createMutation = useIdempotentMutation<SupplierItem, SupplierItemCreate>({
    mutationFn: (input, key) => createSupplierItem(input, key),
    onSuccess: () => {
      setEditorOpen(false);
      invalidate();
    },
    onError: onMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateSupplierItem(
        editingId as string,
        {
          supplier_sku: draft.supplier_sku,
          currency: draft.currency.toUpperCase(),
          minimum_order_qty: toScale(draft.minimum_order_qty, QTY_SCALE),
          pack_size: toScale(draft.pack_size, QTY_SCALE),
          lead_time_days: Number.parseInt(draft.lead_time_days, 10),
          is_preferred: draft.is_preferred,
          is_active: draft.is_active,
          ...(draft.reorder_point_qty === ""
            ? {}
            : { reorder_point_qty: toScale(draft.reorder_point_qty, QTY_SCALE) }),
          ...(draft.safety_stock_qty === ""
            ? {}
            : { safety_stock_qty: toScale(draft.safety_stock_qty, QTY_SCALE) }),
          ...(draft.target_stock_qty === ""
            ? {}
            : { target_stock_qty: toScale(draft.target_stock_qty, QTY_SCALE) }),
          ...(draft.default_order_qty === ""
            ? {}
            : { default_order_qty: toScale(draft.default_order_qty, QTY_SCALE) }),
          ...(draft.over_receipt_tolerance_pct === ""
            ? {}
            : {
                over_receipt_tolerance_pct: toScale(draft.over_receipt_tolerance_pct, PCT_SCALE),
              }),
        },
        editingEtag ?? "",
      ),
    onSuccess: () => {
      setEditorOpen(false);
      invalidate();
    },
    onError: onMutationError,
  });

  const deleteMutation = useMutation({
    // The list carries no per-row validator: read the entity immediately
    // before deleting and forward that resource-bound ETag.
    mutationFn: async (supplierItemId: string) => {
      const { etag } = await getSupplierItem(supplierItemId);
      return deleteSupplierItem(supplierItemId, etag ?? "");
    },
    onSuccess: () => invalidate(),
    onError: onMutationError,
  });

  const save = (): void => {
    setFieldErrors({});
    setDuplicateOf(null);
    const leadTime = Number.parseInt(draft.lead_time_days, 10);
    if (!Number.isInteger(leadTime) || leadTime < 0) {
      setFieldErrors({
        lead_time_days: t(
          "purchasing.supplierItems.leadInvalid",
          "Enter whole days, zero or more.",
        ),
      });
      return;
    }
    if (editingId !== null) {
      updateMutation.mutate();
      return;
    }
    if (draft.supplier_id === "" || draft.variant_id === "") {
      setFieldErrors({
        supplier_id:
          draft.supplier_id === ""
            ? t("purchasing.supplierItems.supplierRequired", "Choose a supplier.")
            : "",
        variant_id:
          draft.variant_id === ""
            ? t("purchasing.supplierItems.variantRequired", "Choose a variant.")
            : "",
      });
      return;
    }
    createMutation.mutate({
      supplier_id: draft.supplier_id,
      variant_id: draft.variant_id,
      supplier_sku: draft.supplier_sku,
      currency: draft.currency.toUpperCase(),
      minimum_order_qty: toScale(draft.minimum_order_qty, QTY_SCALE),
      pack_size: toScale(draft.pack_size, QTY_SCALE),
      lead_time_days: leadTime,
      is_preferred: draft.is_preferred,
      ...(draft.reorder_point_qty === ""
        ? {}
        : { reorder_point_qty: toScale(draft.reorder_point_qty, QTY_SCALE) }),
      ...(draft.safety_stock_qty === ""
        ? {}
        : { safety_stock_qty: toScale(draft.safety_stock_qty, QTY_SCALE) }),
      ...(draft.target_stock_qty === ""
        ? {}
        : { target_stock_qty: toScale(draft.target_stock_qty, QTY_SCALE) }),
      ...(draft.default_order_qty === ""
        ? {}
        : { default_order_qty: toScale(draft.default_order_qty, QTY_SCALE) }),
      ...(draft.over_receipt_tolerance_pct === ""
        ? {}
        : { over_receipt_tolerance_pct: toScale(draft.over_receipt_tolerance_pct, PCT_SCALE) }),
    });
  };

  const renderQty = (value: string | undefined, digits = QTY_SCALE): string => {
    if (value === undefined) return "";
    try {
      return formatQuantity(value, i18n.language, digits);
    } catch {
      return value;
    }
  };

  const columns = useMemo<ColumnDef<SupplierItem>[]>(
    () => [
      {
        accessorKey: "supplier_sku",
        id: "supplier_sku",
        header: t("purchasing.supplierItems.columns.supplierSku", "Supplier SKU"),
        cell: (ctx) => (
          <span dir="ltr" className="font-mono">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "variant_id",
        id: "variant",
        header: t("purchasing.supplierItems.columns.variant", "Variant"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono">
            {variantSku(ctx.getValue<string>())}
          </span>
        ),
      },
      {
        accessorKey: "supplier_id",
        id: "supplier",
        header: t("purchasing.supplierItems.columns.supplier", "Supplier"),
        enableSorting: false,
        cell: (ctx) => textCell(supplierName(ctx.getValue<string>())),
      },
      {
        accessorKey: "last_cost",
        id: "last_cost",
        header: t("purchasing.supplierItems.columns.lastCost", "Last cost"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => {
          const value = ctx.getValue<string | undefined>();
          if (value === undefined) return textCell(undefined);
          return (
            <span dir="ltr" className="tabular-nums">
              {renderQty(value, COST_SCALE)} {ctx.row.original.currency}
            </span>
          );
        },
      },
      {
        accessorKey: "minimum_order_qty",
        id: "minimum_order_qty",
        header: t("purchasing.supplierItems.columns.moq", "MOQ"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => renderQty(ctx.getValue<string>()),
      },
      {
        accessorKey: "pack_size",
        id: "pack_size",
        header: t("purchasing.supplierItems.columns.pack", "Pack"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => renderQty(ctx.getValue<string>()),
      },
      {
        accessorKey: "lead_time_days",
        id: "lead_time_days",
        header: t("purchasing.supplierItems.columns.lead", "Lead days"),
        meta: { numeric: true },
        // Lead time is a whole-day integer, not a money/quantity decimal.
        cell: (ctx) => String(ctx.getValue<number>()),
      },
      {
        accessorKey: "is_preferred",
        id: "is_preferred",
        header: t("purchasing.supplierItems.columns.preferred", "Preferred"),
        enableSorting: false,
        cell: (ctx) =>
          ctx.getValue<boolean>() ? (
            <Badge variant="success">
              {t("purchasing.supplierItems.preferredYes", "Preferred")}
            </Badge>
          ) : (
            textCell(undefined)
          ),
      },
      {
        accessorKey: "updated_at",
        id: "updated_at",
        header: t("purchasing.supplierItems.columns.updated", "Updated"),
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: (ctx) => (
          <span className="flex items-center justify-end gap-1">
            {canUpdate ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t("purchasing.supplierItems.edit", "Edit {{sku}}", {
                  sku: ctx.row.original.supplier_sku,
                })}
                disabled={loadMutation.isPending}
                onClick={(event) => {
                  event.stopPropagation();
                  loadMutation.mutate(ctx.row.original.id);
                }}
              >
                <Pencil className="size-4" aria-hidden="true" />
              </Button>
            ) : null}
            {canDelete ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t("purchasing.supplierItems.delete", "Delete {{sku}}", {
                  sku: ctx.row.original.supplier_sku,
                })}
                disabled={deleteMutation.isPending}
                onClick={(event) => {
                  event.stopPropagation();
                  deleteMutation.mutate(ctx.row.original.id);
                }}
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            ) : null}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- label helpers derive from t and the lookup maps
    [
      t,
      supplierName,
      variantSku,
      canUpdate,
      canDelete,
      loadMutation.isPending,
      deleteMutation.isPending,
    ],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {t("purchasing.supplierItems.title", "Supplier items")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "purchasing.supplierItems.subtitle",
              "Purchasing defaults per supplier and variant. Last cost is maintained by receipt history.",
            )}
          </p>
        </div>
        {canCreate ? (
          <Button
            onClick={() => {
              setEditingId(null);
              setEditingEtag(null);
              setDraft(EMPTY_DRAFT);
              setFieldErrors({});
              setDuplicateOf(null);
              setEditorOpen(true);
            }}
          >
            {t("purchasing.supplierItems.new", "New supplier item")}
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {canReadSuppliers ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-si-supplier">
              {t("purchasing.supplierItems.filters.supplier", "Supplier")}
            </Label>
            <Combobox
              id="purchasing-si-supplier"
              options={parties.map((party) => ({ value: party.id, label: party.display_name }))}
              value={supplierId}
              onChange={setSupplierId}
              placeholder={t("purchasing.common.anySupplier", "Any supplier")}
              searchPlaceholder={t("purchasing.common.searchSupplier", "Search supplier…")}
              emptyMessage={t("purchasing.common.noSuppliers", "No suppliers found.")}
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-si-q">
            {t("purchasing.supplierItems.filters.q", "Supplier SKU")}
          </Label>
          <Input
            id="purchasing-si-q"
            dir="ltr"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder={t("purchasing.supplierItems.filters.qPlaceholder", "Prefix search…")}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-si-preferred">
            {t("purchasing.supplierItems.filters.preferred", "Preferred only")}
          </Label>
          <Select value={preferred} onValueChange={setPreferred}>
            <SelectTrigger id="purchasing-si-preferred">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("purchasing.common.all", "All")}</SelectItem>
              <SelectItem value="yes">{t("purchasing.common.yes", "Yes")}</SelectItem>
              <SelectItem value="no">{t("purchasing.common.no", "No")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {hasFilters ? (
        <div>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => {
              setSupplierId(null);
              setQ("");
              setPreferred(ALL);
            }}
          >
            {t("purchasing.common.clearFilters", "Clear filters")}
          </Button>
        </div>
      ) : null}

      {duplicateOf !== null ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/50 p-2 text-sm text-destructive"
        >
          {t(
            "purchasing.supplierItems.duplicate",
            "This supplier already has a record for that variant.",
          )}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        sort={sort}
        onSortChange={setSort}
        storageKey="purchasing.supplier-items.list.columns"
        emptyMessage={
          hasFilters
            ? t("purchasing.supplierItems.emptyFiltered", "No supplier items match these filters.")
            : t(
                "purchasing.supplierItems.empty",
                "No supplier items yet — they appear as purchase orders are received, and can also be added manually.",
              )
        }
        errorMessage={t(
          "purchasing.supplierItems.loadError",
          "Supplier items could not be loaded.",
        )}
      />

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent closeLabel={t("actions.close")} className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId === null
                ? t("purchasing.supplierItems.new", "New supplier item")
                : t("purchasing.supplierItems.editTitle", "Edit supplier item")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "purchasing.supplierItems.editBody",
                "Last cost is not editable — it is derived from receipt history.",
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {editingId === null ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="purchasing-si-form-supplier">
                    {t("purchasing.supplierItems.supplier", "Supplier")}
                  </Label>
                  <Combobox
                    id="purchasing-si-form-supplier"
                    options={parties.map((party) => ({
                      value: party.id,
                      label: party.display_name,
                    }))}
                    value={draft.supplier_id === "" ? null : draft.supplier_id}
                    onChange={(value) =>
                      setDraft((current) => ({ ...current, supplier_id: value ?? "" }))
                    }
                    placeholder={t("purchasing.common.pickSupplier", "Choose a supplier…")}
                    searchPlaceholder={t("purchasing.common.searchSupplier", "Search supplier…")}
                    emptyMessage={t("purchasing.common.noSuppliers", "No suppliers found.")}
                    aria-invalid={
                      fieldErrors.supplier_id !== undefined && fieldErrors.supplier_id !== ""
                    }
                  />
                  {fieldErrors.supplier_id !== undefined && fieldErrors.supplier_id !== "" ? (
                    <p role="alert" className="text-xs text-destructive">
                      {fieldErrors.supplier_id}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="purchasing-si-form-variant">
                    {t("purchasing.supplierItems.variant", "Variant")}
                  </Label>
                  <Combobox
                    id="purchasing-si-form-variant"
                    options={variants.map((variant) => ({
                      value: variant.id,
                      label: variant.sku,
                    }))}
                    value={draft.variant_id === "" ? null : draft.variant_id}
                    onChange={(value) =>
                      setDraft((current) => ({ ...current, variant_id: value ?? "" }))
                    }
                    placeholder={t("purchasing.common.pickVariant", "Choose a SKU…")}
                    searchPlaceholder={t("purchasing.common.searchSku", "Search SKU…")}
                    emptyMessage={t("purchasing.common.noVariants", "No variants found.")}
                    aria-invalid={
                      fieldErrors.variant_id !== undefined && fieldErrors.variant_id !== ""
                    }
                  />
                  {fieldErrors.variant_id !== undefined && fieldErrors.variant_id !== "" ? (
                    <p role="alert" className="text-xs text-destructive">
                      {fieldErrors.variant_id}
                    </p>
                  ) : null}
                </div>
              </>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-si-form-sku">
                {t("purchasing.supplierItems.supplierSku", "Supplier SKU")}
              </Label>
              <Input
                id="purchasing-si-form-sku"
                dir="ltr"
                value={draft.supplier_sku}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, supplier_sku: event.target.value }))
                }
                aria-invalid={fieldErrors.supplier_sku !== undefined}
              />
              {fieldErrors.supplier_sku !== undefined ? (
                <p role="alert" className="text-xs text-destructive">
                  {fieldErrors.supplier_sku}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-si-form-currency">
                {t("purchasing.supplierItems.currency", "Currency")}
              </Label>
              <Input
                id="purchasing-si-form-currency"
                dir="ltr"
                maxLength={3}
                value={draft.currency}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    currency: event.target.value.toUpperCase(),
                  }))
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-si-form-moq">
                {t("purchasing.supplierItems.moq", "Minimum order quantity")}
              </Label>
              <DecimalInput
                id="purchasing-si-form-moq"
                scale={QTY_SCALE}
                value={draft.minimum_order_qty}
                onChange={(next) =>
                  setDraft((current) => ({ ...current, minimum_order_qty: next }))
                }
                aria-invalid={fieldErrors.minimum_order_qty !== undefined}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-si-form-pack">
                {t("purchasing.supplierItems.pack", "Pack size")}
              </Label>
              <DecimalInput
                id="purchasing-si-form-pack"
                scale={QTY_SCALE}
                value={draft.pack_size}
                onChange={(next) => setDraft((current) => ({ ...current, pack_size: next }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-si-form-lead">
                {t("purchasing.supplierItems.lead", "Lead time (days)")}
              </Label>
              <Input
                id="purchasing-si-form-lead"
                dir="ltr"
                inputMode="numeric"
                value={draft.lead_time_days}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, lead_time_days: event.target.value }))
                }
                aria-invalid={fieldErrors.lead_time_days !== undefined}
              />
              {fieldErrors.lead_time_days !== undefined ? (
                <p role="alert" className="text-xs text-destructive">
                  {fieldErrors.lead_time_days}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-si-form-rop">
                {t("purchasing.supplierItems.reorderPoint", "Reorder point")}
              </Label>
              <DecimalInput
                id="purchasing-si-form-rop"
                scale={QTY_SCALE}
                value={draft.reorder_point_qty}
                onChange={(next) =>
                  setDraft((current) => ({ ...current, reorder_point_qty: next }))
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-si-form-safety">
                {t("purchasing.supplierItems.safetyStock", "Safety stock")}
              </Label>
              <DecimalInput
                id="purchasing-si-form-safety"
                scale={QTY_SCALE}
                value={draft.safety_stock_qty}
                onChange={(next) => setDraft((current) => ({ ...current, safety_stock_qty: next }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-si-form-target">
                {t("purchasing.supplierItems.targetStock", "Target stock")}
              </Label>
              <DecimalInput
                id="purchasing-si-form-target"
                scale={QTY_SCALE}
                value={draft.target_stock_qty}
                onChange={(next) => setDraft((current) => ({ ...current, target_stock_qty: next }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-si-form-tolerance">
                {t("purchasing.supplierItems.tolerance", "Over-receipt tolerance %")}
              </Label>
              <DecimalInput
                id="purchasing-si-form-tolerance"
                scale={PCT_SCALE}
                value={draft.over_receipt_tolerance_pct}
                onChange={(next) =>
                  setDraft((current) => ({ ...current, over_receipt_tolerance_pct: next }))
                }
              />
            </div>
            <Label className="flex items-center gap-2 font-normal">
              <Checkbox
                checked={draft.is_preferred}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({ ...current, is_preferred: checked === true }))
                }
              />
              {t("purchasing.supplierItems.isPreferred", "Preferred supplier for this variant")}
            </Label>
            {editingId !== null ? (
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox
                  checked={draft.is_active}
                  onCheckedChange={(checked) =>
                    setDraft((current) => ({ ...current, is_active: checked === true }))
                  }
                />
                {t("purchasing.supplierItems.isActive", "Active")}
              </Label>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditorOpen(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              loading={createMutation.isPending || updateMutation.isPending}
              disabled={createMutation.isPending || updateMutation.isPending}
              onClick={save}
            >
              {t("actions.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConflictDialog
        open={conflict}
        onReload={() => {
          setConflict(false);
          setEditorOpen(false);
          query.refetch();
        }}
        onDismiss={() => setConflict(false)}
      />
    </div>
  );
}
